import React, { useContext } from "react";
import { SettingsContext } from "./SettingsContext.tsx";
import { SelectButton } from "primereact/selectbutton";
import { RadioButton } from "primereact/radiobutton";
import {
    FractionDenominatorOptions,
    FractionModelMaxDenominatorOptions,
    FractionQuestionOptions,
    isFractionModelQuestion,
    isFractionSubskill,
    MaxDenominatorOptions,
    Operations,
} from "../Constants.ts";
import { withLabels } from "../Labels.ts";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";

/**
 * Settings that only mean anything on a fraction sheet.
 *
 * The question type comes first: arithmetic, or one of the sub-skills
 * (simplify, compare, to a decimal, …). The denominator rule is meaningless for
 * a sub-skill and the largest-denominator choice is filtered rather than
 * honoured for "to a decimal", so both are hidden or annotated (ADR 0021).
 */
export function FractionSettings() {
    const { settings, setSettings } = useContext(SettingsContext);

    const subskill = isFractionSubskill(settings.FractionQuestion);
    const isModel = isFractionModelQuestion(settings.FractionQuestion);
    const needsCommonDenominator =
        !subskill &&
        (settings.Operators?.some(
            (operator) =>
                operator.value === Operations.Addition ||
                operator.value === Operations.Subtraction
        ) ?? false);
    const isToDecimal = settings.FractionQuestion === FractionQuestionOptions.ToDecimal;
    const t = useTranslation();

    function handleQuestionChange(value: FractionQuestionOptions) {
        if (!value) return;
        setSettings({ ...settings, FractionQuestion: value });
    }

    function handleDenominatorRuleChange(value: FractionDenominatorOptions) {
        setSettings({ ...settings, FractionDenominators: value });
    }

    function handleMaxDenominatorChange(event) {
        setSettings({ ...settings, MaxDenominator: Number(event.target.value) });
    }

    function handleModelMaxDenominatorChange(event) {
        setSettings({ ...settings, FractionModelMaxDenominator: Number(event.target.value) });
    }

    return (
        <div className="fractionSettingsContainer">
            <div className="regroupingContainer">
                <SelectButton
                    value={settings.FractionQuestion}
                    onChange={(e) => handleQuestionChange(e.value)}
                    options={withLabels(Object.values(FractionQuestionOptions), settings.Language)}
                    className="select regrouping"
                    aria-label={t('settings.fractionQuestionTypeAria')}
                />
            </div>

            {needsCommonDenominator && (
                <div className="regroupingContainer">
                    <SelectButton
                        value={settings.FractionDenominators}
                        onChange={(e) => handleDenominatorRuleChange(e.value)}
                        options={withLabels(Object.values(FractionDenominatorOptions), settings.Language)}
                        className="select regrouping"
                        aria-label={t('settings.denominatorsAria')}
                    />
                </div>
            )}
            {isModel ? (
                <>
                    <div className="selectContainer">
                        <span className="settingLabel">{t('settings.largestDenominatorLabel')}</span>
                    </div>
                    <div className="radioButtons" role="radiogroup" aria-label={t('settings.largestDenominatorAria')}>
                        {FractionModelMaxDenominatorOptions.map((option) => (
                            <div key={option}>
                                <RadioButton
                                    inputId={`modelMaxDenominator-${option}`}
                                    value={option}
                                    checked={settings.FractionModelMaxDenominator === option}
                                    onChange={handleModelMaxDenominatorChange}
                                />
                                <label htmlFor={`modelMaxDenominator-${option}`}>{option}</label>
                            </div>
                        ))}
                    </div>
                    <p className="settingNote">
                        {t('settings.fractionModelNote')}
                    </p>
                </>
            ) : (
                <>
                    <div className="selectContainer">
                        <span className="settingLabel">{t('settings.largestDenominatorLabel')}</span>
                    </div>
                    <div className="radioButtons" role="radiogroup" aria-label={t('settings.largestDenominatorAria')}>
                        {MaxDenominatorOptions.map((option) => (
                            <div key={option}>
                                <RadioButton
                                    inputId={`maxDenominator-${option}`}
                                    value={option}
                                    checked={settings.MaxDenominator === option}
                                    onChange={handleMaxDenominatorChange}
                                />
                                <label htmlFor={`maxDenominator-${option}`}>{option}</label>
                            </div>
                        ))}
                    </div>
                </>
            )}
            {isToDecimal && (
                <p className="settingNote">
                    {t('settings.fractionToDecimalNote')}
                </p>
            )}
        </div>
    );
}

export default FractionSettings;
